import { useGetCountriesQuery } from 'api/countriesApi'
import { ReactNode, createContext, useContext, useState } from 'react'
import { Country } from 'shared/types/Country'

type AddressFormState = {
  /** Available countries represented by an array of Country objects. */
  availableCountries: Country[]
  /** Dictionary of available countries. Keyed by countryCode (ISO), values are Country objects. */
  countryDict: Record<string, Country>
  /** Actively selected country (ISO code) in the form country dropdown. */
  countryISOCode: string
  /** Indicates if state is required in the form given the selected country. */
  isStateRequired: boolean
  /** Indicates if zip code is required in the form given the selected country. */
  isZipRequired: boolean
  /** Handles state update (i.e. isStateRequired, isZipRequired) when a country is selected.
   * @param countryISOCode selected country (ISO code).
   */
  countryUpdateHandler: (countryISOCode: string) => void
}

export const AddressFormContext = createContext<AddressFormState>({
  availableCountries: [],
  countryDict: {},
  countryISOCode: '',
  isStateRequired: false,
  isZipRequired: false,
  countryUpdateHandler: () => {},
})

export const useAddressFormContext = () => useContext(AddressFormContext)

type AddressFormContextProviderProps = {
  children: ReactNode
}
export const AddressFormContextProvider = ({ children }: AddressFormContextProviderProps) => {
  const [countryISOCode, setCountryISOCode] = useState('')
  const [isStateRequired, setIsStateRequired] = useState(false)
  const [isZipRequired, setIsZipRequired] = useState(true)
  const { isLoading, isError, data } = useGetCountriesQuery()

  if (isLoading || isError) return null

  const availableCountries = data?.countries.filter((country) => country.shipsTo) || []
  const countryDict: Record<string, Country> = availableCountries.reduce(
    (dict: Record<string, Country>, c: Country) => ({ ...dict, [c.isoCode]: c }),
    {},
  )
  const countryUpdateHandler = (countryCode: string) => {
    if (!countryCode) return
    const { stateRequired } = countryDict[countryCode]
    const { zipRequired } = countryDict[countryCode]

    setCountryISOCode(countryCode)
    setIsStateRequired(stateRequired)
    setIsZipRequired(zipRequired)
  }

  const addressFormContextValues: AddressFormState = {
    availableCountries,
    countryDict,
    countryISOCode,
    isStateRequired,
    isZipRequired,
    countryUpdateHandler,
  }

  return (
    <AddressFormContext.Provider value={addressFormContextValues}>
      {children}
    </AddressFormContext.Provider>
  )
}
